import { Link, useNavigate } from "react-router-dom";
import { useCart } from "@/context/CartContext";
import { formatRupiah } from "@/lib/api";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Trash2, Minus, Plus, ShoppingBag, ArrowRight } from "lucide-react";

export default function Cart() {
  const { items, removeItem, updateQty, subtotal } = useCart();
  const navigate = useNavigate();

  return (
    <div className="App">
      <Navbar />
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <h1 className="font-heading text-3xl sm:text-4xl font-bold text-[#00133F] mb-8">Keranjang</h1>

        {items.length === 0 ? (
          <div className="text-center py-24 rounded-2xl border border-dashed border-slate-300">
            <div className="w-16 h-16 rounded-full bg-slate-100 flex items-center justify-center mx-auto mb-5">
              <ShoppingBag className="w-7 h-7 text-slate-400" />
            </div>
            <p className="text-slate-600 font-medium">Keranjangmu masih kosong</p>
            <p className="text-slate-400 text-sm mt-1 mb-6">Yuk, mulai kustomisasi produk favoritmu.</p>
            <Link to="/produk"><Button className="rounded-full bg-[#00133F] text-white" data-testid="cart-shop-btn">Lihat Produk</Button></Link>
          </div>
        ) : (
          <div className="grid lg:grid-cols-3 gap-8">
            <div className="lg:col-span-2 space-y-4">
              {items.map((item) => (
                <div key={item.cart_id} data-testid={`cart-item-${item.cart_id}`}
                  className="flex gap-4 p-4 rounded-2xl border border-slate-200 bg-white">
                  <div className="w-24 h-24 rounded-xl overflow-hidden bg-slate-50 shrink-0">
                    <img src={item.image_url} alt={item.product_name} className="w-full h-full object-contain p-1" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex justify-between gap-2">
                      <div>
                        <h3 className="font-heading font-semibold text-[#00133F]">{item.product_name}</h3>
                        <p className="text-sm text-[#F0005A] font-semibold mt-0.5">{formatRupiah(item.unit_price)}</p>
                        {item.variant_selections?.length > 0 && <p className="text-xs text-slate-500 mt-0.5">{item.variant_selections.map((v) => `${v.group_name}: ${v.option_label}`).join(" · ")}</p>}
                        {item.product_kind === "custom" && !item.design_file_id && <p className="text-xs text-[#25D366] mt-0.5">Desain dikirim via WA</p>}
                        {item.notes && <p className="text-xs text-slate-400 mt-1 line-clamp-1">Catatan: {item.notes}</p>}
                      </div>
                      <button onClick={() => removeItem(item.cart_id)} data-testid={`remove-item-${item.cart_id}`}
                        className="text-slate-400 hover:text-red-500 transition-colors h-fit"><Trash2 className="w-4 h-4" /></button>
                    </div>
                    <div className="flex items-center justify-between mt-3">
                      <div className="flex items-center border border-slate-200 rounded-full">
                        <button className="px-3 py-1.5 hover:bg-slate-50" onClick={() => updateQty(item.cart_id, Math.max(1, item.quantity - 1))} data-testid={`cart-qty-minus-${item.cart_id}`}><Minus className="w-3.5 h-3.5" /></button>
                        <input type="number" min={1} value={item.quantity} data-testid={`cart-qty-input-${item.cart_id}`}
                          onChange={(e) => { const v = parseInt(e.target.value, 10); updateQty(item.cart_id, Number.isNaN(v) ? 1 : Math.max(1, v)); }}
                          className="w-12 text-center text-sm font-semibold outline-none [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none" />
                        <button className="px-3 py-1.5 hover:bg-slate-50" onClick={() => updateQty(item.cart_id, item.quantity + 1)} data-testid={`cart-qty-plus-${item.cart_id}`}><Plus className="w-3.5 h-3.5" /></button>
                      </div>
                      <span className="font-semibold text-[#00133F]">{formatRupiah(item.unit_price * item.quantity)}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="lg:sticky lg:top-24 self-start">
              <div className="rounded-2xl border border-slate-200 bg-white p-6">
                <h3 className="font-heading font-semibold text-[#00133F] text-lg mb-4">Ringkasan</h3>
                <div className="flex justify-between text-sm text-slate-600 mb-2">
                  <span>Subtotal produk</span><span className="font-medium">{formatRupiah(subtotal)}</span>
                </div>
                <div className="flex justify-between text-sm text-slate-400 mb-4">
                  <span>Ongkir</span><span>Dihitung saat checkout</span>
                </div>
                <div className="border-t border-slate-100 pt-4 flex justify-between font-semibold text-[#00133F]">
                  <span>Total sementara</span><span>{formatRupiah(subtotal)}</span>
                </div>
                <Button data-testid="checkout-button" onClick={() => navigate("/checkout")}
                  className="w-full mt-6 h-12 rounded-full bg-[#F0005A] hover:brightness-110 active:scale-95 transition-transform text-white">
                  Lanjut ke Checkout <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
      <Footer />
    </div>
  );
}
